import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Plot from 'react-plotly.js';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';

import { temperatureContext } from '../lib/context';
import { buildDetailSeries } from './detailSeries';

// One page of the reading list. A long record has tens of thousands of readings,
// and rendering them all as MUI rows froze the page for seconds (#274).
const PAGE_SIZE = 100;

function Detail(props: any) {
    const { details, row, tKey, maxTemp, minTemp, bgColor, threshold } = props;
    const { t } = useTranslation(undefined, { keyPrefix: tKey });

    const tempUnit = useContext(temperatureContext);

    const columns: any[] = [
        { id: 'time', label: t('time'), minWidth: 60 },
        { id: 'temperature', label: t('temp'), minWidth: 60 },
    ];

    // Classify and thin the readings once per record, not on every render.
    const series = useMemo(
        () =>
            buildDetailSeries(details, {
                alertStart: threshold.alert_start_time,
                alertEnd: threshold.alert_end_time,
                upperLimit: row.upperLimit,
                lowerLimit: row.lowerLimit,
            }),
        [details, threshold.alert_start_time, threshold.alert_end_time, row.upperLimit, row.lowerLimit]
    );

    const [page, setPage] = useState(0);
    useEffect(() => setPage(0), [details]);
    const pageRows = details.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    return (
        <>
            <div style={{ width: '40%' }}>
                <div style={{ fontSize: '16px', textAlign: 'center', margin: '20px auto' }}>{t('tempRecordList')}</div>
                <TableContainer sx={{ maxHeight: 440 }}>
                    <Table stickyHeader aria-label='sticky table'>
                        <TableHead>
                            <TableRow>
                                {columns.map((column: any) => (
                                    <TableCell
                                        key={column.id}
                                        align={column.align}
                                        style={{ minWidth: column.minWidth }}
                                        sx={{ bgcolor: '#E0EDFB' }}
                                    >
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pageRows.map((row: any) => {
                                // console.log(row)
                                return (
                                    <TableRow hover role='checkbox' tabIndex={-1} key={row.time}>
                                        {columns.map((column: any, index: any) => {
                                            const value = row[column.id];
                                            if (index === 1) {
                                                return (
                                                    <TableCell key={column.id} align={column.align}>
                                                        {/* {column.format && typeof value === 'number'
                                                                    ? column.format(value)
                                                                    : `${value}°C`} */}
                                                        {value !== null && value !== ''
                                                            ? `${value}${tempUnit === 'fahrenheit' ? '°F' : '°C'}`
                                                            : ''}
                                                    </TableCell>
                                                );
                                            }
                                            return (
                                                <TableCell key={column.id} align={column.align}>
                                                    {column.format && typeof value === 'number'
                                                        ? column.format(value)
                                                        : `${value}`}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                {details.length > PAGE_SIZE && (
                    <TablePagination
                        component='div'
                        count={details.length}
                        page={page}
                        rowsPerPage={PAGE_SIZE}
                        rowsPerPageOptions={[]}
                        onPageChange={(_event, next) => setPage(next)}
                    />
                )}
            </div>

            <Plot
                data={[
                    {
                        x: series.line.x,
                        y: series.line.y,
                        type: 'scatter',
                        mode: 'lines',
                        marker: { color: '#E6E6FF' }, //#e6e6ff
                        showlegend: false,
                    },
                    {
                        x: series.normal.x,
                        y: series.normal.y,
                        type: 'scatter',
                        mode: 'markers',
                        marker: { color: '#3F8EE8' },
                        name: t('normal'),
                    },
                    {
                        x: series.abnormal.x,
                        y: series.abnormal.y,
                        type: 'scatter',
                        mode: 'markers',
                        marker: { color: '#FF3838' },
                        name: t('abnormal'),
                    },
                ]}
                layout={{
                    yaxis: { range: [minTemp - 5, maxTemp + 5], fixedrange: true },
                    xaxis: { fixedrange: true },
                    width: 500,
                    height: 550,
                    title: t('trendGraph'),
                    paper_bgcolor: bgColor,
                    plot_bgcolor: bgColor,
                }}
                config={{ displayModeBar: false }}
            />
        </>
    );
}

export default Detail;
